import { useState, useRef, useCallback, useEffect } from "react";
import { Link } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { useAuth } from "@/hooks/use-auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  ArrowLeft, Wrench, ArrowUpDown, Plus, Trash2, ChevronLeft,
  Bold, Italic, Strikethrough, List, ListOrdered, Heading2,
  Hammer, Zap, Droplets, Home, Paintbrush, Building2,
  Layers, LayoutGrid, Leaf, NotebookPen, BookOpen, Ruler, Calculator, Footprints,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import WallFramer from "./wall-framer";
import MaterialCalculators from "./material-calculators";
import StairStringer from "./stair-stringer";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

// ─── Unit converter ──────────────────────────────────────────────────────────

type UnitDef = { label: string; toBase: (v: number) => number; fromBase: (v: number) => number };

const UNIT_CATEGORIES: Record<string, { label: string; units: Record<string, UnitDef> }> = {
  length: {
    label: "Length",
    units: {
      in: { label: "Inches (in)", toBase: (v) => v * 0.0254, fromBase: (v) => v / 0.0254 },
      ft: { label: "Feet (ft)", toBase: (v) => v * 0.3048, fromBase: (v) => v / 0.3048 },
      yd: { label: "Yards (yd)", toBase: (v) => v * 0.9144, fromBase: (v) => v / 0.9144 },
      m: { label: "Meters (m)", toBase: (v) => v, fromBase: (v) => v },
      cm: { label: "Centimeters (cm)", toBase: (v) => v / 100, fromBase: (v) => v * 100 },
      mm: { label: "Millimeters (mm)", toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
    },
  },
  area: {
    label: "Area",
    units: {
      "in²": { label: "Sq. Inches (in²)", toBase: (v) => v * 0.00064516, fromBase: (v) => v / 0.00064516 },
      "ft²": { label: "Sq. Feet (ft²)", toBase: (v) => v * 0.09290304, fromBase: (v) => v / 0.09290304 },
      "yd²": { label: "Sq. Yards (yd²)", toBase: (v) => v * 0.83612736, fromBase: (v) => v / 0.83612736 },
      "m²": { label: "Sq. Meters (m²)", toBase: (v) => v, fromBase: (v) => v },
      acre: { label: "Acres", toBase: (v) => v * 4046.8564224, fromBase: (v) => v / 4046.8564224 },
    },
  },
  volume: {
    label: "Volume",
    units: {
      "fl oz": { label: "Fl. Ounces (fl oz)", toBase: (v) => v * 0.0000295735295625, fromBase: (v) => v / 0.0000295735295625 },
      cup: { label: "Cups", toBase: (v) => v * 0.0002365882365, fromBase: (v) => v / 0.0002365882365 },
      pt: { label: "Pints (pt)", toBase: (v) => v * 0.000473176473, fromBase: (v) => v / 0.000473176473 },
      qt: { label: "Quarts (qt)", toBase: (v) => v * 0.000946352946, fromBase: (v) => v / 0.000946352946 },
      gal: { label: "Gallons (gal)", toBase: (v) => v * 0.003785411784, fromBase: (v) => v / 0.003785411784 },
      "ft³": { label: "Cubic Feet (ft³)", toBase: (v) => v * 0.028316846592, fromBase: (v) => v / 0.028316846592 },
      "yd³": { label: "Cubic Yards (yd³)", toBase: (v) => v * 0.764554857984, fromBase: (v) => v / 0.764554857984 },
      L: { label: "Liters (L)", toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
      mL: { label: "Milliliters (mL)", toBase: (v) => v / 1000000, fromBase: (v) => v * 1000000 },
    },
  },
  weight: {
    label: "Weight",
    units: {
      oz: { label: "Ounces (oz)", toBase: (v) => v * 0.028349523125, fromBase: (v) => v / 0.028349523125 },
      lb: { label: "Pounds (lb)", toBase: (v) => v * 0.45359237, fromBase: (v) => v / 0.45359237 },
      "ton (US)": { label: "Tons (US)", toBase: (v) => v * 907.18474, fromBase: (v) => v / 907.18474 },
      kg: { label: "Kilograms (kg)", toBase: (v) => v, fromBase: (v) => v },
      g: { label: "Grams (g)", toBase: (v) => v / 1000, fromBase: (v) => v * 1000 },
    },
  },
  temperature: {
    label: "Temperature",
    units: {
      "°F": { label: "Fahrenheit (°F)", toBase: (v) => (v - 32) * (5 / 9), fromBase: (v) => v * (9 / 5) + 32 },
      "°C": { label: "Celsius (°C)", toBase: (v) => v, fromBase: (v) => v },
      "°K": { label: "Kelvin (K)", toBase: (v) => v - 273.15, fromBase: (v) => v + 273.15 },
    },
  },
};

function UnitConverter() {
  const [category, setCategory] = useState("length");
  const [fromUnit, setFromUnit] = useState("ft");
  const [toUnit, setToUnit] = useState("in");
  const [inputVal, setInputVal] = useState("1");

  const cat = UNIT_CATEGORIES[category];

  const handleCategoryChange = (newCat: string) => {
    setCategory(newCat);
    const units = Object.keys(UNIT_CATEGORIES[newCat].units);
    setFromUnit(units[0]);
    setToUnit(units[1] ?? units[0]);
    setInputVal("1");
  };

  const compute = () => {
    const num = parseFloat(inputVal);
    if (isNaN(num)) return "—";
    const from = cat.units[fromUnit];
    const to = cat.units[toUnit];
    if (!from || !to) return "—";
    const result = to.fromBase(from.toBase(num));
    if (!isFinite(result)) return "—";
    const abs = Math.abs(result);
    if (abs === 0) return "0";
    if (abs >= 1000) return result.toFixed(2);
    if (abs >= 0.01) return parseFloat(result.toPrecision(6)).toString();
    return result.toExponential(4);
  };

  const swap = () => { setFromUnit(toUnit); setToUnit(fromUnit); };

  const ROW = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" };
  const NUM_STYLE: React.CSSProperties = {
    height: "3rem", fontSize: "1.125rem", fontWeight: 600,
    fontVariantNumeric: "tabular-nums", textAlign: "right",
    border: "1px solid hsl(var(--border))", borderRadius: "0.5rem",
    padding: "0 0.75rem", display: "flex", alignItems: "center",
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {/* Category */}
      <Select value={category} onValueChange={handleCategoryChange}>
        <SelectTrigger style={{ height: "3rem", fontSize: "1rem" }}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {Object.entries(UNIT_CATEGORIES).map(([key, val]) => (
            <SelectItem key={key} value={key}>{val.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* From row — number | unit */}
      <div style={ROW}>
        <Input
          type="number"
          inputMode="decimal"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          placeholder="0"
          style={{ height: "3rem", fontSize: "1.125rem", fontWeight: 600, textAlign: "right" }}
        />
        <Select value={fromUnit} onValueChange={setFromUnit}>
          <SelectTrigger style={{ height: "3rem", fontSize: "0.9375rem" }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(cat.units).map(([key, u]) => (
              <SelectItem key={key} value={key}>{u.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Swap */}
      <button
        onClick={swap}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
          height: "2.5rem", border: "1px solid hsl(var(--border))", borderRadius: "0.5rem",
          backgroundColor: "hsl(var(--muted))", cursor: "pointer",
          fontSize: "0.8125rem", fontWeight: 500, color: "hsl(var(--muted-foreground))", width: "100%",
        }}
        className="hover:bg-accent transition-colors"
      >
        <ArrowUpDown style={{ width: "0.875rem", height: "0.875rem" }} />
        Swap
      </button>

      {/* To row — number | unit */}
      <div style={ROW}>
        <div style={{ ...NUM_STYLE, backgroundColor: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))", border: "none", justifyContent: "flex-end" }}>
          {compute()}
        </div>
        <Select value={toUnit} onValueChange={setToUnit}>
          <SelectTrigger style={{ height: "3rem", fontSize: "0.9375rem" }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(cat.units).map(([key, u]) => (
              <SelectItem key={key} value={key}>{u.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

// ─── Notes app ───────────────────────────────────────────────────────────────

type FieldNote = { id: number; title: string; content: string; updatedAt: string; createdAt: string };

const NOTES_KEY = ["field", "notes"] as const;

function useNotes() {
  return useQuery<FieldNote[]>({
    queryKey: NOTES_KEY,
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/field/notes`);
      if (!res.ok) throw new Error();
      return res.json();
    },
  });
}

function fmtDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function stripHtml(html: string) {
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 80);
}

// Toolbar button — 44px touch target for mobile
function TB({ active, onClick, label, children }: { active?: boolean; onClick: () => void; label: string; children: React.ReactNode }) {
  return (
    <button
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      title={label}
      aria-label={label}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: "2.75rem", height: "2.75rem", borderRadius: "0.5rem", flexShrink: 0,
        backgroundColor: active ? "hsl(var(--primary))" : "transparent",
        border: "1px solid " + (active ? "hsl(var(--primary))" : "transparent"),
        cursor: "pointer",
        color: active ? "hsl(var(--primary-foreground))" : "hsl(var(--foreground))",
      }}
      className="hover:bg-accent hover:text-foreground transition-colors"
    >
      {children}
    </button>
  );
}

function NoteEditor({ note, onBack }: { note: FieldNote | null; onBack: () => void }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved">("idle");

  // Use refs to always have current values without stale closures
  const titleRef = useRef(note?.title ?? "");
  const noteIdRef = useRef<number | null>(note?.id ?? null);
  const isSavingRef = useRef(false);
  const [title, setTitle] = useState(note?.title ?? "");

  const handleTitleChange = (v: string) => {
    setTitle(v);
    titleRef.current = v;
  };

  const editor = useEditor({
    extensions: [StarterKit],
    content: note?.content ?? "",
    editorProps: {
      attributes: {
        style: [
          "min-height: 14rem",
          "padding: 0.875rem",
          "font-size: 1rem",
          "line-height: 1.7",
          "color: hsl(var(--foreground))",
        ].join("; "),
      },
    },
  });

  // Single save function — always reads from refs, so never stale
  const save = useCallback(async () => {
    if (isSavingRef.current) return;
    const content = editor?.getHTML() ?? "";
    const t = titleRef.current;
    // Skip saving completely empty notes
    const isEmpty = !t.trim() && (!content || content === "<p></p>");
    if (isEmpty) return;

    isSavingRef.current = true;
    setSaveState("saving");
    try {
      if (noteIdRef.current === null) {
        const res = await fetch(`${BASE}/api/field/notes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: t, content }),
        });
        if (!res.ok) throw new Error(await res.text());
        const created: FieldNote = await res.json();
        noteIdRef.current = created.id;
      } else {
        const res = await fetch(`${BASE}/api/field/notes/${noteIdRef.current}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: t, content }),
        });
        if (!res.ok) throw new Error(await res.text());
      }
      setSaveState("saved");
      qc.invalidateQueries({ queryKey: NOTES_KEY });
      setTimeout(() => setSaveState("idle"), 2000);
    } catch (err) {
      setSaveState("idle");
      toast({ title: "Failed to save note", variant: "destructive" });
    } finally {
      isSavingRef.current = false;
    }
  }, [editor, qc, toast]);

  // Save on editor blur (when user taps away)
  useEffect(() => {
    if (!editor) return;
    const handleBlur = () => save();
    editor.on("blur", handleBlur);
    return () => { editor.off("blur", handleBlur); };
  }, [editor, save]);

  // Save on unmount (navigation away)
  useEffect(() => {
    return () => { save(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!editor) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      {/* Back + save state */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button
          onClick={onBack}
          style={{
            display: "flex", alignItems: "center", gap: "0.375rem",
            fontSize: "0.9375rem", color: "hsl(var(--muted-foreground))",
            background: "none", border: "none", cursor: "pointer", padding: "0.25rem 0",
          }}
          className="hover:text-foreground transition-colors"
        >
          <ChevronLeft style={{ width: "1.125rem", height: "1.125rem" }} />
          Notes
        </button>
        <span style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>
          {saveState === "saving" ? "Saving…" : saveState === "saved" ? "Saved" : ""}
        </span>
      </div>

      {/* Title */}
      <Input
        value={title}
        onChange={(e) => handleTitleChange(e.target.value)}
        onBlur={save}
        placeholder="Note title"
        style={{ fontSize: "1.125rem", fontWeight: 600, height: "3rem", border: "none", borderBottom: "1px solid hsl(var(--border))", borderRadius: 0, padding: "0 0" }}
      />

      {/* Toolbar */}
      <div style={{
        display: "flex", gap: "0.25rem", flexWrap: "wrap",
        padding: "0.375rem",
        backgroundColor: "hsl(var(--muted))",
        borderRadius: "0.625rem",
        border: "1px solid hsl(var(--border))",
      }}>
        <TB active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} label="Bold">
          <Bold style={{ width: "1.125rem", height: "1.125rem" }} />
        </TB>
        <TB active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} label="Italic">
          <Italic style={{ width: "1.125rem", height: "1.125rem" }} />
        </TB>
        <TB active={editor.isActive("strike")} onClick={() => editor.chain().focus().toggleStrike().run()} label="Strikethrough">
          <Strikethrough style={{ width: "1.125rem", height: "1.125rem" }} />
        </TB>
        <div style={{ width: "1px", backgroundColor: "hsl(var(--border))", alignSelf: "stretch", margin: "0.25rem 0.125rem" }} />
        <TB active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} label="Heading">
          <Heading2 style={{ width: "1.125rem", height: "1.125rem" }} />
        </TB>
        <div style={{ width: "1px", backgroundColor: "hsl(var(--border))", alignSelf: "stretch", margin: "0.25rem 0.125rem" }} />
        <TB active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} label="Bullet list">
          <List style={{ width: "1.125rem", height: "1.125rem" }} />
        </TB>
        <TB active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} label="Numbered list">
          <ListOrdered style={{ width: "1.125rem", height: "1.125rem" }} />
        </TB>
      </div>

      {/* Editor */}
      <div style={{
        border: "1px solid hsl(var(--border))",
        borderRadius: "0.625rem",
        backgroundColor: "hsl(var(--background))",
        overflow: "hidden",
      }}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function NotesApp() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: notes = [], isLoading } = useNotes();
  const [editingNote, setEditingNote] = useState<FieldNote | null | "new">(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetch(`${BASE}/api/field/notes/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: NOTES_KEY });
      setConfirmDelete(null);
      toast({ title: "Note deleted" });
    },
    onError: () => toast({ title: "Failed to delete", variant: "destructive" }),
  });

  // If editing a new note, show the editor immediately
  if (editingNote === "new") {
    return (
      <NoteEditor
        note={null}
        onBack={() => { setEditingNote(null); qc.invalidateQueries({ queryKey: NOTES_KEY }); }}
      />
    );
  }

  // If editing an existing note
  if (editingNote !== null) {
    return (
      <NoteEditor
        note={editingNote}
        onBack={() => { setEditingNote(null); qc.invalidateQueries({ queryKey: NOTES_KEY }); }}
      />
    );
  }

  // List view
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
      <Button onClick={() => setEditingNote("new")} className="w-full" style={{ height: "3rem" }}>
        <Plus style={{ width: "1rem", height: "1rem", marginRight: "0.5rem" }} />
        New Note
      </Button>

      {isLoading ? (
        <p style={{ textAlign: "center", color: "hsl(var(--muted-foreground))", fontSize: "0.875rem", padding: "2rem 0" }}>Loading…</p>
      ) : notes.length === 0 ? (
        <div style={{ textAlign: "center", padding: "2.5rem 1rem", color: "hsl(var(--muted-foreground))" }}>
          <NotebookPen style={{ width: "2rem", height: "2rem", margin: "0 auto 0.5rem", opacity: 0.3 }} />
          <p style={{ fontSize: "0.875rem" }}>No notes yet. Tap New Note to get started.</p>
        </div>
      ) : (
        notes.map((note) => (
          <div
            key={note.id}
            style={{
              border: "1px solid hsl(var(--border))",
              borderRadius: "0.625rem",
              overflow: "hidden",
            }}
          >
            <button
              onClick={() => setEditingNote(note)}
              style={{
                width: "100%", textAlign: "left", padding: "0.875rem 1rem",
                background: "none", border: "none", cursor: "pointer",
                display: "block",
              }}
              className="hover:bg-muted transition-colors"
            >
              <div style={{ fontWeight: 600, fontSize: "0.9375rem", marginBottom: "0.25rem", color: "hsl(var(--foreground))" }}>
                {note.title || <span style={{ fontStyle: "italic", opacity: 0.5 }}>Untitled</span>}
              </div>
              <div style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.375rem" }}>
                {stripHtml(note.content) || <span style={{ fontStyle: "italic" }}>No content</span>}
              </div>
              <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>
                {fmtDate(note.updatedAt)}
              </div>
            </button>
            {confirmDelete === note.id ? (
              <div style={{
                display: "flex", alignItems: "center", gap: "0.5rem",
                padding: "0.5rem 1rem", backgroundColor: "hsl(var(--muted))",
                borderTop: "1px solid hsl(var(--border))",
              }}>
                <span style={{ fontSize: "0.8125rem", flex: 1, color: "hsl(var(--muted-foreground))" }}>Delete this note?</span>
                <Button size="sm" variant="destructive" style={{ height: "1.75rem" }}
                  onClick={() => deleteMutation.mutate(note.id)}
                  disabled={deleteMutation.isPending}
                >
                  Delete
                </Button>
                <Button size="sm" variant="ghost" style={{ height: "1.75rem" }}
                  onClick={() => setConfirmDelete(null)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div style={{ borderTop: "1px solid hsl(var(--border))", padding: "0.375rem 0.75rem" }}>
                <button
                  onClick={() => setConfirmDelete(note.id)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "0.25rem", color: "hsl(var(--muted-foreground))" }}
                >
                  <Trash2 style={{ width: "0.875rem", height: "0.875rem" }} />
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

// ─── Reference table helpers ──────────────────────────────────────────────────

function RefTable({ headers, rows }: { headers: string[]; rows: (string | number)[][] }) {
  return (
    <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" as React.CSSProperties["WebkitOverflowScrolling"] }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{ textAlign: "left", padding: "0.5rem 0.75rem", backgroundColor: "hsl(var(--muted))", borderBottom: "2px solid hsl(var(--border))", fontWeight: 600, whiteSpace: "nowrap", fontSize: "0.8125rem" }}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} style={{ borderBottom: "1px solid hsl(var(--border))" }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{ padding: "0.625rem 0.75rem", verticalAlign: "top", color: ci === 0 ? "hsl(var(--foreground))" : "hsl(var(--muted-foreground))", fontWeight: ci === 0 ? 600 : 400, lineHeight: 1.4 }}>
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionNote({ children }: { children: React.ReactNode }) {
  return <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", margin: "0.625rem 0 0", fontStyle: "italic", lineHeight: 1.5 }}>{children}</p>;
}
function SubHeading({ children }: { children: React.ReactNode }) {
  return <p style={{ fontSize: "0.75rem", fontWeight: 700, color: "hsl(var(--muted-foreground))", textTransform: "uppercase", letterSpacing: "0.07em", margin: "1.25rem 0 0.375rem" }}>{children}</p>;
}

// ─── Trade reference data ─────────────────────────────────────────────────────

const TRADE_REFS = [
  {
    id: "framing", title: "Framing & Carpentry",
    icon: <Hammer style={{ width: "1rem", height: "1rem", flexShrink: 0 }} />,
    content: (
      <>
        <SubHeading>Actual Lumber Dimensions</SubHeading>
        <RefTable headers={["Nominal", "Actual (in)"]} rows={[["1×2","¾ × 1½"],["1×4","¾ × 3½"],["1×6","¾ × 5½"],["1×8","¾ × 7¼"],["1×10","¾ × 9¼"],["1×12","¾ × 11¼"],["2×4","1½ × 3½"],["2×6","1½ × 5½"],["2×8","1½ × 7¼"],["2×10","1½ × 9¼"],["2×12","1½ × 11¼"],["4×4","3½ × 3½"],["4×6","3½ × 5½"],["6×6","5½ × 5½"]]} />
        <SubHeading>Stud Spacing</SubHeading>
        <RefTable headers={["Spacing","Common Use"]} rows={[["12\" OC","Load-bearing, tile backing"],["16\" OC","Standard walls"],["19.2\" OC","Engineered floor systems"],["24\" OC","Non-load-bearing (code-specific)"]]} />
        <SectionNote>16" OC ≈ 7 studs per 8 ft wall; 24" OC ≈ 5 studs (plus corners/openings).</SectionNote>
        <SubHeading>Rule-of-Thumb Header Sizes</SubHeading>
        <RefTable headers={["Opening","Non-LB","Load-Bearing"]} rows={[["Up to 3′","2×4 flat","2×6 or 4×4"],["3′–4′","2×4 flat","2×8"],["4′–5′","2×6 flat","2×10"],["5′–6′","2×6","2×12 or 4×10"],["6′–8′","2×8","Engineer required"]]} />
        <SectionNote>Always verify with local code and a structural engineer for load-bearing applications.</SectionNote>
      </>
    ),
  },
  {
    id: "electrical", title: "Electrical",
    icon: <Zap style={{ width: "1rem", height: "1rem", flexShrink: 0 }} />,
    content: (
      <>
        <SubHeading>Wire Gauge & Ampacity (copper)</SubHeading>
        <RefTable headers={["AWG","60°C","75°C","Common Use"]} rows={[["14","15 A","20 A","Lighting / outlets"],["12","20 A","25 A","Kitchen, bath, garage"],["10","30 A","35 A","Dryers, A/C, water heaters"],["8","40 A","50 A","Ranges, large A/C"],["6","55 A","65 A","EV chargers, sub-panels"],["4","70 A","85 A","Large sub-panels"],["2","95 A","115 A","Sub-panels"],["1/0","125 A","150 A","Main service entrance"],["2/0","145 A","175 A","Main service entrance"],["3/0","165 A","200 A","200 A service"]]} />
        <SectionNote>Derate for conduit fill and ambient temp above 86°F.</SectionNote>
        <SubHeading>Circuit Breaker Sizes</SubHeading>
        <RefTable headers={["Breaker","AWG","Typical Load"]} rows={[["15 A","#14","Lighting, general outlets"],["20 A","#12","Kitchen, bath, garage"],["30 A","#10","Dryer, water heater, A/C"],["40 A","#8","Electric range"],["50 A","#6–8","Range/oven, EV level 2"],["60 A","#6","Hot tub, sub-panel feed"],["100 A","#1–2","Small sub-panels"],["200 A","#2/0–3/0","Main residential service"]]} />
        <SubHeading>Outlet & Switch Heights (AFF)</SubHeading>
        <RefTable headers={["Device","Height"]} rows={[["Duplex outlet","12–18\" (std: 12\")"],["Kitchen counter outlet","44–48\""],["Switch / dimmer","48\" (ADA: 48\" max)"],["Thermostat","48–60\""],["Panel (top breaker)","6′7\" max per NEC"]]} />
      </>
    ),
  },
  {
    id: "plumbing", title: "Plumbing & HVAC",
    icon: <Droplets style={{ width: "1rem", height: "1rem", flexShrink: 0 }} />,
    content: (
      <>
        <SubHeading>Common Pipe Sizes</SubHeading>
        <RefTable headers={["Size","Material","Use"]} rows={[["½\"","Copper / PEX","Branch supply lines"],["¾\"","Copper / PEX","Main supply runs"],["1\"","Copper / PEX","Main service entry"],["1¼\"","PVC/ABS","Lav drain, P-traps"],["1½\"","PVC/ABS","Sink drain, washer"],["2\"","PVC/ABS","Shower drain, vents"],["3\"","PVC/ABS","Toilet, branch drains"],["4\"","PVC/ABS","Main drain / sewer"]]} />
        <SubHeading>Drain Slope</SubHeading>
        <RefTable headers={["Pipe Size","Min Slope"]} rows={[["2½\" and smaller","¼\" per foot (2%)"],["3\" and larger","⅛\" per foot (1%)"]]} />
        <SectionNote>IPC/UPC reference. &gt;½" per foot can cause solids to stall.</SectionNote>
        <SubHeading>HVAC BTU Rule of Thumb</SubHeading>
        <RefTable headers={["Climate Zone","BTU / sq ft"]} rows={[["Hot/Humid (South)","20–25"],["Mixed (Midwest)","25–30"],["Cold (Northeast)","30–35"],["Very cold","35–40"]]} />
        <SectionNote>Rule-of-thumb only. Manual J required for accurate sizing. 1 ton = 12,000 BTU/hr.</SectionNote>
        <SubHeading>Drain Fixture Units (DFU)</SubHeading>
        <RefTable headers={["Fixture","DFU"]} rows={[["Bathtub / shower","2"],["Toilet","3–4"],["Lavatory","1"],["Kitchen sink","2"],["Dishwasher","2"],["Clothes washer","3"],["Floor drain","2"]]} />
      </>
    ),
  },
  {
    id: "roofing", title: "Roofing",
    icon: <Home style={{ width: "1rem", height: "1rem", flexShrink: 0 }} />,
    content: (
      <>
        <SubHeading>Pitch Chart</SubHeading>
        <RefTable headers={["Pitch","Degrees","Multiplier"]} rows={[["2:12","9.5°","1.014"],["3:12","14°","1.031"],["4:12","18.4°","1.054"],["5:12","22.6°","1.083"],["6:12","26.6°","1.118"],["7:12","30.3°","1.158"],["8:12","33.7°","1.202"],["9:12","36.9°","1.250"],["10:12","39.8°","1.302"],["12:12","45°","1.414"]]} />
        <SectionNote>Multiplier × plan area = actual roof area. Add 10–15% for hips/valleys.</SectionNote>
        <SubHeading>Shingle Coverage</SubHeading>
        <RefTable headers={["Material","Coverage"]} rows={[["3-tab / architectural","100 sq ft / square"],["1 square","~3 bundles"],["Ridge cap","~25–35 LF / bundle"],["Ice & water shield","~200 sq ft / roll (sizes vary)"],["Felt 15#","400 sq ft / roll"],["Felt 30#","200 sq ft / roll"]]} />
        <SubHeading>Nailing Pattern</SubHeading>
        <RefTable headers={["Wind Zone","Nails","Type"]} rows={[["< 60 mph","4 per shingle","1¼\" roofing nail"],["60–110 mph","6 per shingle","1¼\" or longer"],["Starter strip","2 per 12\" OC","Near leading edge"]]} />
      </>
    ),
  },
  {
    id: "painting", title: "Painting & Finishing",
    icon: <Paintbrush style={{ width: "1rem", height: "1rem", flexShrink: 0 }} />,
    content: (
      <>
        <SubHeading>Paint Coverage (per gallon)</SubHeading>
        <RefTable headers={["Product","Coverage"]} rows={[["Flat / Matte","350–400 sq ft"],["Eggshell / Satin","300–350 sq ft"],["Semi-gloss / Gloss","250–300 sq ft"],["Primer","250–400 sq ft"],["Exterior paint","250–300 sq ft"],["Solid stain","150–250 sq ft"],["Semi-transparent stain","200–300 sq ft"]]} />
        <SectionNote>Calculate 2 coats for new drywall or color changes. Add 10–15% for cut-in waste.</SectionNote>
        <SubHeading>Sheen Guide</SubHeading>
        <RefTable headers={["Sheen","Best For"]} rows={[["Flat / Matte","Ceilings, low-traffic walls"],["Eggshell","Living rooms, bedrooms"],["Satin","Hallways, kitchens"],["Semi-gloss","Trim, doors, bath"],["Gloss","Cabinets, trim accents"]]} />
        <SubHeading>Drywall Coat Sequence</SubHeading>
        <RefTable headers={["Coat","Product"]} rows={[["Tape coat","All-purpose / taping mud"],["Fill coat","All-purpose / topping"],["Finish coat","Lightweight / topping"],["Prime","PVA drywall primer"]]} />
      </>
    ),
  },
  {
    id: "concrete", title: "Concrete & Masonry",
    icon: <Building2 style={{ width: "1rem", height: "1rem", flexShrink: 0 }} />,
    content: (
      <>
        <SubHeading>Mix Strengths</SubHeading>
        <RefTable headers={["PSI","Use"]} rows={[["2,500","Footings (residential)"],["3,000","Slabs, driveways, sidewalks"],["3,500","Traffic slabs, garage floors"],["4,000","High-strength / structural"],["5,000+","Commercial, high-load"]]} />
        <SubHeading>Bag Coverage</SubHeading>
        <RefTable headers={["Bag","Yield","At 4\"","At 6\""]} rows={[["60 lb","0.45 ft³","~1.35 sq ft","~0.9 sq ft"],["80 lb","0.60 ft³","~1.8 sq ft","~1.2 sq ft"]]} />
        <SectionNote>Formula: (L × W × depth in ft) ÷ yield per bag. Order 5–10% extra.</SectionNote>
        <SubHeading>CMU Block Sizes</SubHeading>
        <RefTable headers={["Nominal","Actual"]} rows={[["4\" CMU","3⅝\" × 7⅝\" × 15⅝\""],["6\" CMU","5⅝\" × 7⅝\" × 15⅝\""],["8\" CMU","7⅝\" × 7⅝\" × 15⅝\""],["12\" CMU","11⅝\" × 7⅝\" × 15⅝\""]]} />
        <SubHeading>Cure Times</SubHeading>
        <RefTable headers={["Time","Strength","Notes"]} rows={[["24–48 hrs","~25–40%","Careful foot traffic"],["7 days","~65–70%","Light vehicle traffic"],["28 days","100%","Full design strength"],["90 days",">100%","Continued gain"]]} />
      </>
    ),
  },
  {
    id: "drywall", title: "Drywall",
    icon: <Layers style={{ width: "1rem", height: "1rem", flexShrink: 0 }} />,
    content: (
      <>
        <SubHeading>Sheet Sizes</SubHeading>
        <RefTable headers={["Size","Thickness","Use"]} rows={[["4×8","⅜\"","Curved walls, patches"],["4×8","½\"","Standard walls & ceilings"],["4×12","½\"","Fewer seams on tall walls"],["4×8","⅝\"","Ceilings 24\" OC, fire walls"],["4×8","⅝\" Type X","1-hr fire / garages"],["4×8","½\" MR","Damp areas, not behind tile in wet zones"]]} />
        <SubHeading>Screw Spacing</SubHeading>
        <RefTable headers={["Location","Framing","Spacing"]} rows={[["Walls","16\" OC","16\" OC field / 8\" edges"],["Walls","24\" OC","12\" OC field / 8\" edges"],["Ceilings","16\" OC","12\" OC field / 8\" edges"],["Ceilings","24\" OC","12\" OC field / 8\" edges"]]} />
        <SubHeading>Coverage Formulas</SubHeading>
        <RefTable headers={["Item","Rule"]} rows={[["Sheets (4×8)","Total sq ft ÷ 32, + 10–15% waste"],["Screws (1 lb)","~200 sq ft of ½\" board"],["Joint compound","~1 gal per 100 sq ft (3 coats)"],["Tape (paper)","~250 ft / roll; ~1 roll per 550 sq ft"]]} />
      </>
    ),
  },
  {
    id: "flooring", title: "Flooring",
    icon: <LayoutGrid style={{ width: "1rem", height: "1rem", flexShrink: 0 }} />,
    content: (
      <>
        <SubHeading>Waste Factors</SubHeading>
        <RefTable headers={["Material","Waste"]} rows={[["Hardwood (straight)","7–10%"],["Hardwood (diagonal)","12–15%"],["Laminate / LVP (straight)","7%"],["Laminate / LVP (diagonal)","10%"],["Tile (straight)","10%"],["Tile (diagonal)","15%"],["Tile (complex)","15–20%"],["Carpet / sheet vinyl","10–15%"]]} />
        <SubHeading>Tile Grout Joint Width</SubHeading>
        <RefTable headers={["Tile Size","Joint"]} rows={[["1–3\" mosaic","⅛\""],["4×4 to 6×6","⅛–3/16\""],["12×12 to 18×18","3/16–¼\""],["20×20+","¼–⅜\""],["Rectified (any)","⅛\" min"]]} />
        <SubHeading>Underlayment</SubHeading>
        <RefTable headers={["Floor Type","Underlayment"]} rows={[["Hardwood nail-down","None"],["Laminate","2–3mm foam + vapor barrier"],["LVP/LVT","Check product — many have attached"],["Carpet","6–8 lb rebond pad"],["Ceramic / porcelain tile","Cement board or Ditra"],["Vinyl sheet","Lauan or self-leveling"]]} />
      </>
    ),
  },
  {
    id: "landscaping", title: "Landscaping",
    icon: <Leaf style={{ width: "1rem", height: "1rem", flexShrink: 0 }} />,
    content: (
      <>
        <SubHeading>Mulch & Topsoil (1 cubic yard)</SubHeading>
        <RefTable headers={["Depth","Coverage"]} rows={[["1\"","324 sq ft"],["2\"","162 sq ft"],["3\"","108 sq ft (typical mulch)"],["4\"","81 sq ft"],["6\"","54 sq ft"],["12\"","27 sq ft"]]} />
        <SectionNote>Formula: sq ft × depth (in) ÷ 324 = cubic yards. Add 10–15% for settling.</SectionNote>
        <SubHeading>Sod Coverage</SubHeading>
        <RefTable headers={["Unit","Coverage"]} rows={[["1 pallet","450–500 sq ft"],["Roll 18\"×24\"","3 sq ft"],["Roll 24\"×48\"","8 sq ft"]]} />
        <SubHeading>Gravel (¾\" crushed, per ton at 1.5 tons / yd³)</SubHeading>
        <RefTable headers={["Depth","Coverage"]} rows={[["1\"","~216 sq ft"],["2\"","~108 sq ft"],["3\"","~72 sq ft (driveway base)"],["4\"","~54 sq ft"]]} />
        <SectionNote>1 cu yd crushed stone ≈ 1.5 tons. Verify with supplier.</SectionNote>
        <SubHeading>Plant Spacing</SubHeading>
        <RefTable headers={["Spacing","Plants / 100 sq ft"]} rows={[["6\"","400"],["12\"","100"],["18\"","44"],["24\"","25"],["36\"","11"]]} />
      </>
    ),
  },
];

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FieldToolsPage() {
  usePageTitle("Field Tools");
  const { user } = useAuth();

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: "1.25rem" }}>
        <Link href="/field">
          <button
            style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.9375rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.875rem", background: "none", border: "none", cursor: "pointer", padding: "0.25rem 0" }}
            className="hover:text-foreground transition-colors"
          >
            <ArrowLeft style={{ width: "1.125rem", height: "1.125rem" }} />
            Field
          </button>
        </Link>
        <h1 style={{ fontSize: "1.75rem", fontWeight: 700, display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <Wrench style={{ width: "1.5rem", height: "1.5rem", color: "hsl(var(--primary))" }} />
          Field Tools
        </h1>
      </div>

      {/* Main tabs */}
      <Tabs defaultValue="tools">
        <TabsList className="w-full" style={{ marginBottom: "1.25rem" }}>
          <TabsTrigger value="tools" className="flex-1" style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
            <Wrench style={{ width: "0.875rem", height: "0.875rem" }} />
            Tools
          </TabsTrigger>
          <TabsTrigger value="reference" className="flex-1" style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
            <BookOpen style={{ width: "0.875rem", height: "0.875rem" }} />
            Reference
          </TabsTrigger>
        </TabsList>

        {/* Tools tab */}
        <TabsContent value="tools">
          <Accordion type="multiple" defaultValue={["notes"]} className="w-full" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>

            <AccordionItem value="notes" style={{ border: "1px solid hsl(var(--border))", borderRadius: "0.625rem", overflow: "hidden" }}>
              <AccordionTrigger style={{ padding: "1rem 1.125rem", fontWeight: 600, fontSize: "0.9375rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
                  <NotebookPen style={{ width: "1rem", height: "1rem", color: "hsl(var(--primary))", flexShrink: 0 }} />
                  Notes
                </div>
              </AccordionTrigger>
              <AccordionContent style={{ padding: "0 1.125rem 1.125rem" }}>
                {user ? <NotesApp /> : <p style={{ color: "hsl(var(--muted-foreground))", fontSize: "0.875rem" }}>Sign in to use notes.</p>}
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="converter" style={{ border: "1px solid hsl(var(--border))", borderRadius: "0.625rem", overflow: "hidden" }}>
              <AccordionTrigger style={{ padding: "1rem 1.125rem", fontWeight: 600, fontSize: "0.9375rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
                  <Wrench style={{ width: "1rem", height: "1rem", color: "hsl(var(--primary))", flexShrink: 0 }} />
                  Unit Converter
                </div>
              </AccordionTrigger>
              <AccordionContent style={{ padding: "0 1.125rem 1.125rem" }}>
                <UnitConverter />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="materials" style={{ border: "1px solid hsl(var(--border))", borderRadius: "0.625rem", overflow: "hidden" }}>
              <AccordionTrigger style={{ padding: "1rem 1.125rem", fontWeight: 600, fontSize: "0.9375rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
                  <Calculator style={{ width: "1rem", height: "1rem", color: "hsl(var(--primary))", flexShrink: 0 }} />
                  Material Calculator
                </div>
              </AccordionTrigger>
              <AccordionContent style={{ padding: "0 1.125rem 1.125rem" }}>
                <MaterialCalculators />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="framer" style={{ border: "1px solid hsl(var(--border))", borderRadius: "0.625rem", overflow: "hidden" }}>
              <AccordionTrigger style={{ padding: "1rem 1.125rem", fontWeight: 600, fontSize: "0.9375rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
                  <Ruler style={{ width: "1rem", height: "1rem", color: "hsl(var(--primary))", flexShrink: 0 }} />
                  Wall Framer
                </div>
              </AccordionTrigger>
              <AccordionContent style={{ padding: "0 1.125rem 1.125rem" }}>
                <WallFramer />
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="stairs" style={{ border: "1px solid hsl(var(--border))", borderRadius: "0.625rem", overflow: "hidden" }}>
              <AccordionTrigger style={{ padding: "1rem 1.125rem", fontWeight: 600, fontSize: "0.9375rem" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.625rem" }}>
                  <Footprints style={{ width: "1rem", height: "1rem", color: "hsl(var(--primary))", flexShrink: 0 }} />
                  Stair Stringer
                </div>
              </AccordionTrigger>
              <AccordionContent style={{ padding: "0 1.125rem 1.125rem" }}>
                <StairStringer />
              </AccordionContent>
            </AccordionItem>

          </Accordion>
        </TabsContent>

        {/* Reference tab */}
        <TabsContent value="reference">
          <p style={{ fontSize: "0.8125rem", color: "hsl(var(--muted-foreground))", margin: "0 0 0.75rem", lineHeight: 1.5 }}>
            <strong>For reference only.</strong> These charts are general guides for planning and estimating. Codes vary by location and change over time, so check your local code before you build.
          </p>
          <Accordion type="multiple" className="w-full" style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {TRADE_REFS.map((trade) => (
              <AccordionItem key={trade.id} value={trade.id} style={{ border: "1px solid hsl(var(--border))", borderRadius: "0.625rem", overflow: "hidden" }}>
                <AccordionTrigger style={{ padding: "1rem 1.125rem", fontWeight: 600, fontSize: "0.9375rem" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.625rem", color: "hsl(var(--foreground))" }}>
                    {trade.icon}
                    {trade.title}
                  </div>
                </AccordionTrigger>
                <AccordionContent style={{ padding: "0 1.125rem 1.125rem" }}>
                  {trade.content}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </TabsContent>
      </Tabs>
    </div>
  );
}
